/**
 * Multi-value entry field — commits typed tokens (emails, tags) into
 * removable chips. Used by the invite flow.
 * Spec: assets/handoffs/design_system_export/specs/ChipsInput.json
 *
 * Controlled: `value` is the committed token array, `onChange` receives the
 * next array. `validate` and `maxItems` are optional guards (format check /
 * seat quota) — the spec calls both out under `behavior` without naming props.
 *
 * @param {string[]=[]} value
 * @param {function} onChange - (nextValue: string[]) => void
 * @param {string=} label
 * @param {string=} placeholder
 * @param {number=} maxItems
 * @param {(token: string) => boolean=} validate
 */
import React, { forwardRef, useId, useRef, useState } from "react";
import classNames from "classnames";
import Icon from "../icons/Icon";
import styles from "./ChipsInput.module.scss";

const ChipsInput = forwardRef(
  (
    {
      value = [],
      onChange,
      label,
      placeholder,
      maxItems,
      validate,
      className,
      id,
      ...rest
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState("");
    const [message, setMessage] = useState("");
    const inputRef = useRef(null);
    const autoId = useId();
    const inputId = id || autoId;

    const setRefs = (node) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    const commit = (raw) => {
      const token = raw.trim();
      if (!token) return;
      if (value.includes(token)) {
        setMessage(`${token} is already added.`);
        return;
      }
      if (validate && !validate(token)) {
        setMessage(`${token} isn't valid.`);
        return;
      }
      if (maxItems && value.length >= maxItems) {
        setMessage(`You've reached the limit of ${maxItems}.`);
        return;
      }
      onChange?.([...value, token]);
      setMessage(`${token} added.`);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        commit(inputValue);
        setInputValue("");
      } else if (e.key === "Backspace" && inputValue === "" && value.length > 0) {
        onChange?.(value.slice(0, -1));
      }
    };

    const handlePaste = (e) => {
      const text = e.clipboardData.getData("text");
      if (/[,\n]/.test(text)) {
        e.preventDefault();
        text.split(/[,\n]/).forEach(commit);
        setInputValue("");
      }
    };

    const removeChip = (token) => {
      onChange?.(value.filter((v) => v !== token));
    };

    return (
      <div className={classNames(styles.root, className)}>
        {label && (
          <label className={styles.label} htmlFor={inputId}>
            {label}
          </label>
        )}
        <div
          role="group"
          aria-label={label}
          className={styles.field}
          onClick={() => inputRef.current?.focus()}
        >
          {value.map((token) => (
            <span key={token} className={styles.chip}>
              <span className={styles.chipLabel}>{token}</span>
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove ${token}`}
                onClick={(e) => {
                  e.stopPropagation();
                  removeChip(token);
                }}
              >
                <Icon name="XClose" size={14} />
              </button>
            </span>
          ))}
          <input
            ref={setRefs}
            id={inputId}
            className={styles.input}
            value={inputValue}
            placeholder={value.length === 0 ? placeholder : undefined}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            {...rest}
          />
        </div>
        <span className={styles.liveRegion} role="status" aria-live="polite">
          {message}
        </span>
      </div>
    );
  }
);

ChipsInput.displayName = "ChipsInput";

export default ChipsInput;
